import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, FileText, Inbox, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ quotes: [], contacts: [] });
  const [fetching, setFetching] = useState(true);

  useEffect(() => { if (!loading && !user) navigate("/admin/login"); }, [loading, user, navigate]);
  useEffect(() => {
    if (user) api.get("/admin/submissions").then((res) => setData(res.data)).catch(() => {}).finally(() => setFetching(false));
  }, [user]);

  if (loading || !user) return <div className="flex min-h-screen items-center justify-center bg-[#090B0E] text-slate-400">Laden...</div>;

  const stats = [
    { icon: FileText, label: "Offerteaanvragen", value: data.quotes.length },
    { icon: Inbox, label: "Contactberichten", value: data.contacts.length },
  ];

  return (
    <div className="min-h-screen bg-[#090B0E]" data-testid="admin-page">
      <header className="border-b border-white/10 bg-[#12151D]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-10">
          <div className="flex items-center gap-3">
            <img src="/imperium-logo.jpg" alt="Imperium Group logo" className="h-10 w-10 rounded-lg object-cover ring-1 ring-[#FF4500]/40" />
            <div>
              <div className="font-display text-lg font-medium text-white">Imperium Group Dashboard</div>
              <div className="text-xs text-slate-400">Ingelogd als {user.email}</div>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-[#FF4500] hover:text-[#FF4500]">
            <LogOut className="h-4 w-4" /> Uitloggen
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {stats.map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#12151D] p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF4500]/10 text-[#FF4500]"><Icon className="h-6 w-6" /></span>
                <div>
                  <div className="font-display text-3xl font-bold text-white">{s.value}</div>
                  <div className="font-mono text-xs uppercase tracking-[0.15em] text-slate-400">{s.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        <Tabs defaultValue="quotes" className="mt-10">
          <TabsList className="bg-[#12151D]">
            <TabsTrigger value="quotes" data-testid="tab-quotes">Offertes</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contact</TabsTrigger>
          </TabsList>

          <TabsContent value="quotes" className="mt-6">
            <SubmissionList items={data.quotes} empty={fetching ? "Laden..." : "Nog geen offerteaanvragen."} render={(a) => (
              <>
                <Row label="Naam" value={a.name} />
                <Row label="Bedrijf" value={a.company} />
                <Row label="E-mail" value={a.email} email />
                <Row label="Telefoon" value={a.phone} />
                <Row label="Diensten" value={(a.services || []).join(", ")} full />
                <Row label="Budget" value={a.budget} />
                <Row label="Startdatum" value={a.timeline} />
                <Row label="Toelichting" value={a.message} full />
              </>
            )} />
          </TabsContent>

          <TabsContent value="contacts" className="mt-6">
            <SubmissionList items={data.contacts} empty={fetching ? "Laden..." : "Nog geen contactberichten."} render={(c) => (
              <>
                <Row label="Naam" value={c.name} />
                <Row label="Bedrijf" value={c.company} />
                <Row label="E-mail" value={c.email} email />
                <Row label="Telefoon" value={c.phone} />
                <Row label="Bericht" value={c.message} full />
              </>
            )} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function SubmissionList({ items, render, empty }) {
  if (!items.length) return <div className="rounded-2xl border border-white/10 bg-[#12151D] p-10 text-center text-slate-400">{empty}</div>;
  return (
    <div className="space-y-4">
      {items.map((item, i) => (
        <div key={i} className="rounded-2xl border border-white/10 bg-[#12151D] p-6" data-testid={`submission-${i}`}>
          <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
            <span className="font-display text-white">{item.name}</span>
            <span className="text-xs text-slate-500">{fmtDate(item.created_at)}</span>
          </div>
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">{render(item)}</div>
        </div>
      ))}
    </div>
  );
}

function Row({ label, value, email, full }) {
  if (!value) return null;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <span className="font-mono text-xs uppercase tracking-[0.15em] text-slate-500">{label}</span>
      <div className="mt-1 text-sm text-slate-200">
        {email ? <a href={`mailto:${value}`} className="inline-flex items-center gap-1 text-[#FF4500] hover:underline"><Mail className="h-3.5 w-3.5" />{value}</a> : value}
      </div>
    </div>
  );
}
